import type { ReactNode } from 'react';
import { App } from 'antd';
import { useHotkeyActions, type BoundAction } from '@/shared/lib/hotkeys';
import { useLatest } from '@/shared/lib/hooks';
import type { LinesGridApi } from './LinesGrid';
import { INVALID_INPUTS_HINT, commitActiveField } from './inputs';
import { useUnsavedClose } from './useUnsavedClose';

export interface UseEditorWindowOptions {
  /** В окне есть несохранённые изменения. */
  dirty: boolean;
  /** Подпись «Сохранить» (кнопка, подсказка, диалог): «Сохранить», «Сохранить (3)». */
  saveLabel: string;
  /** Почему сохранить сейчас нельзя («Введите название»); `null` — можно. */
  saveBlockReason: string | null;
  saving?: boolean;
  /** Сохранить. Окно закрывает себя само при успехе, а при ошибке остаётся открытым. */
  onSave: () => unknown;
  /** Закрыть окно по-настоящему (без вопросов). */
  onClose: () => void;
  /** Таблица строк окна: `Insert` добавляет строку, `F8` удаляет строку с курсором. */
  gridApi?: LinesGridApi;
  /** Второстепенное действие `Alt+Delete` («В архив», «Удалить»); необратимое делает своё подтверждение. */
  archive?: { label: string; run: () => void; disabled?: string | false };
}

export interface EditorWindow {
  /** Для `Esc`, крестика и кнопки «Закрыть»: спросит о несохранённом. */
  requestClose: () => void;
  /** Диалог «Есть несохранённые изменения» — вывести рядом с `FloatingWindow`. */
  dialog: ReactNode;
  /** В проп `hotkeys` у `FloatingWindow`. */
  bindings: ReturnType<typeof useHotkeyActions>['bindings'];
  save: BoundAction;
  removeRow: BoundAction;
  archive: BoundAction;
}

/**
 * Клавиши и закрытие «простого» окна-редактора (создание групп, категорий, контрагентов, правка
 * категории): `Ctrl+Enter` и `Ctrl+S` сохраняют, `Insert`/`F8` — строки таблицы, `Alt+Delete` —
 * архив/удаление, `Esc` — закрыть с вопросом о несохранённом. Клавиша сначала фиксирует поле, в
 * котором курсор, и только потом проверяет, можно ли сохранять (подробно — в DocumentWindow).
 */
export function useEditorWindow({ dirty, saveLabel, saveBlockReason, saving, onSave, onClose, gridApi, archive }: UseEditorWindowOptions): EditorWindow {
  const { message } = App.useApp();
  const latest = useLatest({ onSave, saveBlockReason, archive });

  const trySave = () => {
    const { onSave: save, saveBlockReason: reason } = latest.current;
    if (reason !== null) {
      message.warning(reason);
      return;
    }
    void save();
  };

  const { requestClose, dialog } = useUnsavedClose({ dirty, onClose, onSave: trySave, saveLabel, saving });

  const committed = (run: () => void) => () => {
    if (commitActiveField().invalid) {
      message.warning(INVALID_INPUTS_HINT);
      return;
    }
    run();
  };

  const actions = useHotkeyActions({
    'editor.save': { label: saveLabel, alwaysRun: true, disabled: saveBlockReason ?? false, run: committed(trySave) },
    ...(gridApi
      ? {
          'grid.addRow': { run: () => gridApi.addRow() },
          'grid.removeRow': { run: () => gridApi.removeFocusedRow() },
        }
      : {}),
    ...(archive ? { 'editor.archive': { label: archive.label, hint: false, disabled: archive.disabled ?? false, run: committed(() => latest.current.archive?.run()) } } : {}),
  });

  return {
    requestClose,
    dialog,
    bindings: actions.bindings,
    save: actions.get('editor.save'),
    removeRow: actions.get('grid.removeRow'),
    archive: actions.get('editor.archive'),
  };
}
